import {useEffect, useState} from 'react';

export type PortfolioTheme = 'light' | 'dark';

const PORTFOLIO_THEME_STORAGE_KEY = 'portfolioTheme';
const PORTFOLIO_THEME_COLORS: Record<PortfolioTheme, string> = {
    light: '#35476b',
    dark: '#263958',
};

export function resolvePortfolioTheme(
    storedTheme: string | null,
    systemPrefersDark: boolean,
): PortfolioTheme {
    if (storedTheme === 'light' || storedTheme === 'dark') return storedTheme;
    return systemPrefersDark ? 'dark' : 'light';
}

export function usePortfolioTheme() {
    const [theme, setTheme] = useState<PortfolioTheme>(() => resolvePortfolioTheme(
        window.localStorage.getItem(PORTFOLIO_THEME_STORAGE_KEY),
        window.matchMedia('(prefers-color-scheme: dark)').matches,
    ));

    function toggleTheme() {
        setTheme((currentTheme) => {
            const nextTheme = currentTheme === 'light' ? 'dark' : 'light';
            window.localStorage.setItem(PORTFOLIO_THEME_STORAGE_KEY, nextTheme);
            return nextTheme;
        });
    }

    useEffect(() => {
        const systemTheme = window.matchMedia('(prefers-color-scheme: dark)');
        const followSystemTheme = (event: MediaQueryListEvent) => {
            if (window.localStorage.getItem(PORTFOLIO_THEME_STORAGE_KEY)) return;
            setTheme(event.matches ? 'dark' : 'light');
        };

        systemTheme.addEventListener('change', followSystemTheme);
        return () => systemTheme.removeEventListener('change', followSystemTheme);
    }, []);

    useEffect(() => {
        document.documentElement.dataset.theme = theme;
        document.documentElement.style.colorScheme = theme;
        document.querySelector('meta[name="theme-color"]')
            ?.setAttribute('content', PORTFOLIO_THEME_COLORS[theme]);
    }, [theme]);

    return {theme, toggleTheme};
}
